import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Button, Input, Card, MultiImageUploader, DarkModeToggleCompact } from '../../components/ui';
import { ArrowLeft, Save, Palette, Image as ImageIcon, LayoutTemplate, Upload, Trash2, Shield, Calendar, Plus, X, Sparkles } from 'lucide-react';
import { AIStylingDesigner } from '../../components/tools/AIStylingDesigner';
import { useTranslation } from 'react-i18next';

export const LiveDesignPage = () => {
    const { t } = useTranslation()
    const { tournamentId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Form State
    const [themeColor, setThemeColor] = useState('#ea580c');
    const [adminMenuColored, setAdminMenuColored] = useState(false);
    const [bgImage, setBgImage] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [sponsorLeft, setSponsorLeft] = useState('');
    const [sponsorRight, setSponsorRight] = useState('');
    const [sportType, setSportType] = useState('goals');

    useEffect(() => {
        fetchTournamentSettings();
    }, [tournamentId]);

    const fetchTournamentSettings = async () => {
        try {
            const { data } = await supabase
                .from('tournaments')
                .select('theme_color, background_image, logo_url, sponsor_left_image, sponsor_right_image, admin_menu_colored, sport_type')
                .eq('id', tournamentId)
                .single();

            if (data) {
                setThemeColor(data.theme_color || '#ea580c');
                setBgImage(data.background_image || '');
                setLogoUrl(data.logo_url || '');
                setSponsorLeft(data.sponsor_left_image || '');
                setSponsorRight(data.sponsor_right_image || '');
                setAdminMenuColored(data.admin_menu_colored || false);
                setSportType(data.sport_type || 'goals');
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('tournaments')
                .update({
                    theme_color: themeColor,
                    admin_menu_colored: adminMenuColored,
                    background_image: bgImage,
                    logo_url: logoUrl,
                    sponsor_left_image: sponsorLeft,
                    sponsor_right_image: sponsorRight
                })
                .eq('id', tournamentId);

            if (error) throw error;
            alert(t('live_design_page.save_success'));
        } catch (error) {
            console.error('Error saving settings:', error);
            alert(t('live_design_page.save_error') + error.message);
        } finally {
            setSaving(false);
        }
    };

    const isEsports = sportType === 'esports';

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <div className="premium-container animate-fadeIn">
                {/* Header */}
                <div className="mb-8 animate-slideUp">
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate(`/admin/tournaments/${tournamentId}/divisions`)} 
                        className={`mb-4 ${isEsports ? 'text-slate-300 hover:text-white hover:bg-white/5' : ''}`}
                    >
                        <ArrowLeft size={16} className="mr-2" />
                        {t('live_design_page.back_to_tournament')}
                    </Button>

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className={`p-3.5 rounded-2xl shadow-lg transition-all duration-500 hover:rotate-12 ${
                                isEsports 
                                    ? 'bg-gradient-to-br from-purple-500 to-indigo-600 shadow-[0_0_20px_rgba(168,85,247,0.4)]' 
                                    : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_0_20px_rgba(79,70,229,0.3)]'
                            }`}>
                                <Palette className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className={`text-5xl font-bold leading-tight ${isEsports ? 'text-white' : 'text-gray-900'}`}>
                                    {t('live_design_page.title')}
                                </h1>
                                <p className={`text-lg mt-1 ${isEsports ? 'text-slate-400' : 'text-gray-600'}`}>
                                    {t('live_design_page.subtitle')}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <DarkModeToggleCompact />
                            <Button
                                variant="secondary"
                                onClick={() => window.open(`/live/${tournamentId}`, '_blank')}
                                className="py-3.5 px-5 rounded-xl border border-gray-200/20 font-bold"
                            >
                                <Upload size={18} className="mr-2 rotate-90" /> {t('live_design_page.btn_view_live')}
                            </Button>
                            <Button 
                                onClick={handleSave} 
                                disabled={saving} 
                                variant="primary"
                                className={`py-3.5 px-6 rounded-xl font-bold flex items-center gap-2 shadow-lg ${
                                    isEsports ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]' : 'bg-green-600 hover:bg-green-700 text-white'
                                }`}
                            >
                                <Save size={20} />
                                {saving ? t('common.saving') : t('common.save')}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* AI Designer Tool */}
                <div className="mb-8 animate-slideUp">
                    <AIStylingDesigner onApplyTheme={(color) => setThemeColor(color)} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-slideUp">
                    {/* Settings Column */}
                    <div className="space-y-8">
                        {/* Logo Upload */}
                        <div className={`rounded-3xl border shadow-xl p-6 sm:p-8 ${
                            isEsports ? 'bg-purple-950/10 border-purple-500/20 text-white' : 'bg-white border-gray-100'
                        }`}>
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200/10">
                                <ImageIcon size={22} className={isEsports ? 'text-purple-400' : 'text-indigo-500'} />
                                <h3 className="text-xl font-bold">{t('live_design_page.card_logo_title')}</h3>
                            </div>
                            <p className={`text-sm mb-6 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                                {t('live_design_page.card_logo_desc')}
                            </p>
                            <MultiImageUploader
                                label={t('live_design_page.input_logo_label')}
                                value={logoUrl}
                                onChange={setLogoUrl}
                                placeholder={t('live_design_page.input_logo_placeholder')}
                                previewHeight="h-32"
                                allowMultiple={false}
                            />
                        </div>

                        {/* Theme Color */}
                        <div className={`rounded-3xl border shadow-xl p-6 sm:p-8 ${
                            isEsports ? 'bg-purple-950/10 border-purple-500/20 text-white' : 'bg-white border-gray-100'
                        }`}>
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200/10">
                                <Palette size={22} className={isEsports ? 'text-purple-400' : 'text-indigo-500'} />
                                <h3 className="text-xl font-bold">{t('live_design_page.card_theme_title')}</h3>
                            </div>
                            <div className="flex gap-6 items-center mb-6">
                                <input
                                    type="color"
                                    value={themeColor}
                                    onChange={(e) => setThemeColor(e.target.value)}
                                    className="h-16 w-28 cursor-pointer rounded-2xl border-2 border-gray-200/20 bg-transparent p-1 shadow-inner"
                                />
                                <div className={`text-sm ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                                    {t('live_design_page.theme_desc')} <br />
                                    <span style={{ color: themeColor }} className="font-black text-lg block mt-1">
                                        {t('live_design_page.theme_preview_text')}
                                    </span>
                                </div>
                            </div>

                            <label className={`flex items-center gap-4 p-4 rounded-2xl border cursor-pointer transition-all duration-300 ${
                                isEsports 
                                    ? 'bg-purple-950/20 border-purple-500/15 hover:bg-purple-950/30' 
                                    : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
                            }`}>
                                <input
                                    type="checkbox"
                                    className="w-5 h-5 rounded-lg border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    checked={adminMenuColored}
                                    onChange={(e) => setAdminMenuColored(e.target.checked)}
                                />
                                <div>
                                    <span className={`block font-bold text-sm ${isEsports ? 'text-white' : 'text-gray-800'}`}>
                                        {t('live_design_page.admin_menu_label')}
                                    </span>
                                    <span className={`block text-xs mt-0.5 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                                        {t('live_design_page.admin_menu_desc')}
                                    </span>
                                </div>
                            </label>
                        </div>

                        {/* Background Image */}
                        <div className={`rounded-3xl border shadow-xl p-6 sm:p-8 ${
                            isEsports ? 'bg-purple-950/10 border-purple-500/20 text-white' : 'bg-white border-gray-100'
                        }`}>
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200/10">
                                <ImageIcon size={22} className={isEsports ? 'text-purple-400' : 'text-indigo-500'} />
                                <h3 className="text-xl font-bold">{t('live_design_page.card_bg_title')}</h3>
                            </div>
                            <div className={`border rounded-2xl p-4 text-xs mb-6 ${
                                isEsports ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-300' : 'bg-yellow-50 border-yellow-250 text-yellow-800'
                            }`}>
                                <strong>Tip:</strong> <span dangerouslySetInnerHTML={{ __html: t('live_design_page.bg_tip') }} />
                            </div>

                            <MultiImageUploader
                                label={t('live_design_page.input_bg_label')}
                                value={bgImage}
                                onChange={setBgImage}
                                placeholder={t('live_design_page.input_bg_placeholder')}
                                previewHeight="h-48"
                                allowMultiple={false}
                            />
                            <p className="text-xs text-gray-400 mt-3 font-medium">{t('live_design_page.bg_size_tip')}</p>
                        </div>
                    </div>

                    {/* Logo's Column */}
                    <div className="space-y-8">
                        <div className={`rounded-3xl border shadow-xl p-6 sm:p-8 ${
                            isEsports ? 'bg-purple-950/10 border-purple-500/20 text-white' : 'bg-white border-gray-100'
                        }`}>
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200/10">
                                <LayoutTemplate size={22} className={isEsports ? 'text-purple-400' : 'text-indigo-500'} />
                                <h3 className="text-xl font-bold">{t('live_design_page.card_sidebars_title')}</h3>
                            </div>
                            <p className={`text-sm mb-6 ${isEsports ? 'text-slate-400' : 'text-gray-500'}`}>
                                <span dangerouslySetInnerHTML={{ __html: t('live_design_page.sidebars_desc') }} />
                            </p>

                            <div className="space-y-8">
                                <div className="border-b border-gray-200/10 pb-6">
                                    <h4 className="font-bold text-sm mb-4 flex items-center gap-2">
                                        <Shield size={16} className={isEsports ? 'text-purple-400' : 'text-indigo-500'} /> 
                                        {t('live_design_page.left_banner_title')}
                                    </h4>
                                    <MultiImageUploader
                                        value={sponsorLeft}
                                        onChange={setSponsorLeft}
                                        placeholder={t('live_design_page.left_banner_placeholder')}
                                        allowMultiple={true}
                                    />
                                </div>

                                <div>
                                    <h4 className="font-bold text-sm mb-4 flex items-center gap-2">
                                        <Calendar size={16} className={isEsports ? 'text-purple-400' : 'text-indigo-500'} /> 
                                        {t('live_design_page.right_banner_title')}
                                    </h4>
                                    <MultiImageUploader
                                        value={sponsorRight}
                                        onChange={setSponsorRight}
                                        placeholder={t('live_design_page.right_banner_placeholder')}
                                        allowMultiple={true}
                                    />
                                </div>
                            </div>

                            <div className={`mt-8 p-4 rounded-2xl border text-sm ${
                                isEsports ? 'bg-indigo-500/10 border-indigo-500/20 text-indigo-300' : 'bg-indigo-50 border-indigo-100 text-indigo-800'
                            }`}>
                                <strong>Tip:</strong> {t('live_design_page.sidebars_tip')}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
